import type { FieldsGroupRestrictionPayload, FormField } from '@/forms/typings';
import { limitRuleKey } from '@/forms/utils/createLimitPairsYup';
import { requiredWhenValueKey } from '@/forms/utils/createRequiredWhenValueYup';
import { FORM_FIELD_CHILD_LIST_KEYS } from '@/forms/utils/formFieldChildKeys';

type GroupLike = Record<string, unknown> & {
  fieldId?: string;
  restriction?: FieldsGroupRestrictionPayload;
};

type StampableChild = FormField & {
  restrictionErrorKey?: string;
  disabledWhenFieldId?: string;
};

/** Every direct child of the group carrying this fieldId, across all child collections. */
const findChildren = (group: GroupLike, fieldId: string): StampableChild[] => {
  const found: StampableChild[] = [];

  for (const key of FORM_FIELD_CHILD_LIST_KEYS) {
    const children = group[key];

    if (!Array.isArray(children)) continue;

    for (const child of children as StampableChild[])
      if (child?.fieldId === fieldId) found.push(child);
  }

  return found;
};

/** Points the RequiredWhenValue rule's error at the single child it guards. */
const stampRequiredWhenValue = (group: GroupLike): void => {
  const rule = group.restriction?.requiredWhenValue;

  if (!rule || !group.fieldId) return;

  const guardedFieldId = `${group.fieldId}_${rule.requiredFieldItemID}`;
  const matches = findChildren(group, guardedFieldId);

  for (const child of matches)
    child.restrictionErrorKey = requiredWhenValueKey(group.fieldId);

  // Only SingleFieldPresenter renders the stamped key, so a rule guarding a child
  // it cannot reach would drop the message silently. Fail loudly instead.
  if (!matches.length)
    console.warn(
      'RequiredWhenValue restriction could not be attached to its guarded field:',
      guardedFieldId,
    );
};

/** Points each pair's rules at its two amounts, and names the toggle that governs them. */
const stampLimitPairs = (group: GroupLike): void => {
  const rule = group.restriction?.limitPairs;

  if (!rule || !group.fieldId) return;

  for (const pair of rule.pairs) {
    const toggleFieldId = pair.unlimitedItemID
      ? `${group.fieldId}_${pair.unlimitedItemID}`
      : undefined;

    for (const amountItemID of [pair.lowerItemID, pair.upperItemID]) {
      const amountFieldId = `${group.fieldId}_${amountItemID}`;
      const matches = findChildren(group, amountFieldId);

      if (!matches.length) {
        console.warn(
          'LimitPairs restriction could not be attached to its amount field:',
          amountFieldId,
        );
        continue;
      }

      for (const child of matches) {
        // One slot per child: two rules guarding the same field would silently
        // hide one another's message.
        if (child.restrictionErrorKey)
          console.warn(
            'Two group restrictions guard the same field; the later one wins:',
            amountFieldId,
          );

        child.restrictionErrorKey = limitRuleKey(amountFieldId);

        if (toggleFieldId) child.disabledWhenFieldId = toggleFieldId;
      }
    }
  }
};

/**
 * Points child-scoped restriction errors at the fields they guard, so their
 * messages render beside those fields instead of at group level.
 *
 * Mutates the freshly-built children in place — callers invoke this on the result
 * of their own `build()`, so nothing else holds a reference yet.
 */
export const stampGuardedFieldError = <T>(group: T): T => {
  const groupLike = group as GroupLike;

  stampRequiredWhenValue(groupLike);
  stampLimitPairs(groupLike);

  return group;
};
